import './styles.scss'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useInView } from '../../Hooks/useInView'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import { useState } from 'react'

const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_CONTACT
const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SUBJECTS = [
  'General Inquiry',
  'Request a Quote',
  'Air Freight',
  'Ocean Freight',
  'Cargo Transportation',
  'Packaging & Storage',
  'Shipment Tracking Issue',
  'Partnership / Business',
  'Other',
]

const ContactUs = () => {
  const [ref, inView] = useInView()
  const [submitted, setSubmitted] = useState(false)

  const { form, reset, onChange } = useForm({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)

    try {
      const { loading, ...payload } = form
      await emailjs.send(serviceId, templateId, payload, publicId)
      setSubmitted(true)
      reset()
    } catch (error) {
      console.log('ERROR', error)
    }

    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
        <p>Have a question or need a quote? Our team is ready to help.</p>
      </div>

      <div className="contact-us__content">
        <section ref={ref} className="contact-us__content__details">

          {/* ── Left: contact info ── */}
          <div className={`contact-us__info reveal fade-left${inView ? ' visible' : ''}`}>
            <h2 className="contact-us__info__title">Get In Touch</h2>
            <p className="contact-us__info__desc">
              Expeditors Logistics is available around the clock to meet your
              shipping and courier needs. Reach out — we'd love to hear from you.
            </p>

            <div className="contact-us__info__items">
              <div className={`contact-us__info__item reveal fade-up delay-1${inView ? ' visible' : ''}`}>
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('phone')} />
                </div>
                <div>
                  <span className="item-label">Phone</span>
                  <span className="item-value">+1 (212) 235-2008</span>
                </div>
              </div>

              <div className={`contact-us__info__item reveal fade-up delay-2${inView ? ' visible' : ''}`}>
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={regular('envelope')} />
                </div>
                <div>
                  <span className="item-label">Email</span>
                  <span className="item-value">business@expeditorslogistics.live</span>
                </div>
              </div>

              <div className={`contact-us__info__item reveal fade-up delay-3${inView ? ' visible' : ''}`}>
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('location-dot')} />
                </div>
                <div>
                  <span className="item-label">Location</span>
                  <span className="item-value">Global Facility Office</span>
                </div>
              </div>

              <div className={`contact-us__info__item reveal fade-up delay-4${inView ? ' visible' : ''}`}>
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('clock')} />
                </div>
                <div>
                  <span className="item-label">Business Hours</span>
                  <span className="item-value">Mon – Fri: 8:00 AM – 6:00 PM</span>
                  <span className="item-value item-value--sub">24/7 Emergency Support Available</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: contact form ── */}
          <div className={`contact-us__form reveal fade-right${inView ? ' visible' : ''}`}>
            {submitted ? (
              <div className="contact-us__form__success">
                <div className="contact-us__form__success__icon">
                  <FontAwesomeIcon icon={solid('circle-check')} />
                </div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. A member of our team will get back to you within 1 business day.</p>
                <button className="contact-us__form__success__back" onClick={() => setSubmitted(false)}>
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className="contact-us__form__header">
                  <h2 className="contact-us__form__title">Send Us a Message</h2>
                  <p className="contact-us__form__subtitle">Fields marked <span>*</span> are required</p>
                </div>

                <form className="contact-us__form__body" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="cf-name">Full Name <span>*</span></label>
                      <input
                        id="cf-name"
                        name="name"
                        type="text"
                        required
                        placeholder="John Doe"
                        value={form.name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="cf-company">Company Name</label>
                      <input
                        id="cf-company"
                        name="company"
                        type="text"
                        placeholder="Acme Corp (optional)"
                        value={form.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="cf-email">Email Address <span>*</span></label>
                      <input
                        id="cf-email"
                        name="email"
                        type="email"
                        required
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="cf-phone">Phone Number</label>
                      <input
                        id="cf-phone"
                        name="phone"
                        type="tel"
                        placeholder="+1 (000) 000-0000"
                        value={form.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="cf-subject">Subject <span>*</span></label>
                    <select
                      id="cf-subject"
                      name="subject"
                      required
                      value={form.subject}
                      onChange={handleChange}
                    >
                      <option value="" disabled>Select an inquiry type...</option>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label htmlFor="cf-msg">Message <span>*</span></label>
                    <textarea
                      id="cf-msg"
                      name="msg"
                      required
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      value={form.msg}
                      onChange={handleChange}
                    />
                  </div>

                  <button
                    type="submit"
                    className="contact-us__form__submit"
                    disabled={form.loading}
                  >
                    {form.loading ? (
                      <>
                        <FontAwesomeIcon icon={solid('circle-notch')} spin />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={regular('paper-plane')} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

        </section>

        <UnbeatableSection />
      </div>
    </div>
  )
}

export default ContactUs
